/**
 * TeamInitializer component.
 *
 * Automatically loads the user's teams on mount and sets the current team
 * context if not already set. This ensures the team store is populated
 * after login or page refresh.
 *
 * Includes retry logic with exponential backoff to handle transient
 * network errors or stale cached responses.
 */

import { useEffect, useRef } from 'react'

import { teamsApi } from '@/api/teams'
import { useAuthStore } from '@/store/auth'
import { useTeamStore } from '@/store/team'

const MAX_RETRIES = 3
const BASE_DELAY_MS = 1000

export function TeamInitializer({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token)
  const currentTeamId = useTeamStore((s) => s.currentTeamId)
  const setCurrentTeam = useTeamStore((s) => s.setCurrentTeam)
  const setTeams = useTeamStore((s) => s.setTeams)
  const clearTeamContext = useTeamStore((s) => s.clearTeamContext)
  const retryCount = useRef(0)

  useEffect(() => {
    if (!token) return

    retryCount.current = 0

    const loadTeams = async () => {
      while (retryCount.current <= MAX_RETRIES) {
        try {
          const teams = await teamsApi.listTeams()

          setTeams(teams ?? [])

          if (teams && teams.length > 0) {
            // Validate that the persisted currentTeamId belongs to this user's teams.
            // If not (e.g., different user logged in on same browser), reset it.
            const needsTeamSelection = !currentTeamId || !teams.some((t) => t.id === currentTeamId)

            if (needsTeamSelection) {
              // Prefer the team flagged as default, else first team
              const defaultTeam = teams.find((t) => t.isDefault) || teams[0]
              if (!defaultTeam) return
              setCurrentTeam(defaultTeam, defaultTeam.myRole)
            } else {
              // Sync role from fresh data for already-selected team
              const current = teams.find((t) => t.id === currentTeamId)
              if (current) {
                setCurrentTeam(current, current.myRole)
              }
            }
          } else {
            // No teams — clear stale context from localStorage
            clearTeamContext()
          }
          // Success — break out of retry loop
          return
        } catch (error) {
          retryCount.current++
          if (retryCount.current > MAX_RETRIES) {
            console.error(
              `Failed to load teams after ${MAX_RETRIES} retries:`,
              error
            )
            return
          }
          // Exponential backoff: 1s, 2s, 4s
          const delay = BASE_DELAY_MS * Math.pow(2, retryCount.current - 1)
          await new Promise((resolve) => setTimeout(resolve, delay))
        }
      }
    }

    loadTeams()
  }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  return <>{children}</>
}
